import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Validation from "../../Validation/SignupValidation";
import { userRegistration } from "../../services/Auth";
import { useHistory } from "react-router";

const BRAND_RED = "#C62828";
const PANEL_DARK = "#1a2744";

function Signup() {
  const history = useHistory();
  const [showError, setShowError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    type: "STAFF_USER",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    setValues({ ...values, [event.target.name]: event.target.value });
    if (errors[event.target.name]) {
      setErrors({ ...errors, [event.target.name]: undefined });
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = Validation(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      const result = await userRegistration(values);
      setLoading(false);
      if (result === "done") {
        history.push("/");
      } else {
        setShowError(result);
      }
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": { borderColor: BRAND_RED },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: BRAND_RED },
    "& .MuiInputBase-input": { color: "#111111" },
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Left Brand Panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "38%",
          background: `linear-gradient(160deg, ${PANEL_DARK} 0%, #0d1422 100%)`,
          position: "relative",
          overflow: "hidden",
          p: 5,
        }}
      >
        {/* Decorative rings */}
        <Box sx={{
          position: "absolute", width: 340, height: 340, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.18)", top: -100, right: -80,
        }} />
        <Box sx={{
          position: "absolute", width: 200, height: 200, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.12)", top: -20, right: 10,
        }} />
        <Box sx={{
          position: "absolute", width: 220, height: 220, borderRadius: "50%",
          border: "1.5px solid rgba(198,40,40,0.14)", bottom: -60, left: -70,
        }} />
        <Box sx={{
          position: "absolute", width: 80, height: 80, borderRadius: "50%",
          bgcolor: "rgba(198,40,40,0.08)", bottom: 160, right: 50,
        }} />

        {/* Logo card */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            px: 3.5,
            py: 2.5,
            mb: 5,
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
            width: "78%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/images/logo.png"
            alt="INORTEL"
            style={{ width: "100%", maxWidth: 190, height: "auto" }}
          />
        </Box>

        <Typography variant="h5" sx={{ color: "white", fontWeight: 700, textAlign: "center", mb: 2 }}>
          Create Account
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.55)", textAlign: "center", lineHeight: 1.9, maxWidth: 260 }}
        >
          Join the INORTEL platform to manage sales, inventory, and more with your team.
        </Typography>

        {/* Red accent bar */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, bgcolor: BRAND_RED }} />
      </Box>

      {/* ── Right Form Panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#ffffff",
          overflowY: "auto",
          p: { xs: 3, sm: 5, md: 6 },
        }}
      >
        {/* Mobile-only logo */}
        <Box sx={{ display: { xs: "flex", md: "none" }, mb: 3, justifyContent: "center" }}>
          <img src="/images/logo.png" alt="INORTEL" style={{ height: 48 }} />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 500, py: { xs: 2, md: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: PANEL_DARK, mb: 0.5 }}>
            Sign Up
          </Typography>
          <Typography variant="body2" sx={{ color: "#607d8b", mb: 3.5 }}>
            Fill in your details to create a new account
          </Typography>

          {showError && (
            <Alert
              severity="error"
              sx={{ mb: 2.5, borderRadius: 2 }}
              onClose={() => setShowError("")}
            >
              {showError}
            </Alert>
          )}

          <form onSubmit={handleFormSubmit} noValidate>
            <Grid container spacing={2}>
              {/* First Name & Last Name side by side */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  variant="outlined"
                  value={values.firstName}
                  onChange={handleChange}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: errors.firstName ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  variant="outlined"
                  value={values.lastName}
                  onChange={handleChange}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: errors.lastName ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* User Type */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="User Role"
                  name="type"
                  variant="outlined"
                  value={values.type}
                  onChange={handleChange}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeOutlinedIcon sx={{ color: "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="STAFF_USER">Staff User</MenuItem>
                  <MenuItem value="SALES_PERSON">Sales Person</MenuItem>
                </TextField>
              </Grid>

              {/* Phone */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  variant="outlined"
                  value={values.phone}
                  onChange={handleChange}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon sx={{ color: errors.phone ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  variant="outlined"
                  value={values.email}
                  onChange={handleChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ color: errors.email ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={values.password}
                  onChange={handleChange}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: errors.password ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                          {showPassword
                            ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                            : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  variant="outlined"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: errors.confirmPassword ? "error.main" : "#90a4ae", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small">
                          {showConfirm
                            ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                            : <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    bgcolor: BRAND_RED,
                    "&:hover": { bgcolor: "#b71c1c" },
                    borderRadius: 2,
                    py: 1.5,
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    boxShadow: "0 4px 14px rgba(198,40,40,0.35)",
                    textTransform: "none",
                    mt: 0.5,
                  }}
                >
                  {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Create Account"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Typography variant="body2" align="center" sx={{ color: "#607d8b", mt: 3 }}>
            Already have an account?{" "}
            <Link href="/" underline="hover" sx={{ color: BRAND_RED, fontWeight: 600 }}>
              Sign in
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default Signup;
